import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/ui/badge'
import { Text } from '@/components/ui/Text'

import { GateNoMatch } from './gateArtifacts'
import { TWIN_TEST_MAX_QUESTIONS } from './gateQuestionEdits'
import { gateQuestionTypeLabel } from './twinTestGateAdapter'

/**
 * The questions the seam refused, shown at the gate — the only place the
 * caveat can do its job.
 *
 * WHY IT IS HERE AT ALL. The writer asked for something the engine cannot run,
 * the seam refused on shape, and the run went ahead with what survived. Without
 * this block the reviewer reads a conversation that raised five questions,
 * approves a card that lists four, and has no way to tell that the fifth was
 * dropped rather than merged. A silent drop is the one failure a reviewer
 * cannot detect, so it is written out.
 *
 * NOT APPROVABLE, NOT PRICED. These sit outside the numbered question list and
 * outside the card's question count, and `runSpendLine` counts only `questions`,
 * so nothing here moves the number somebody is authorising. The visual language
 * says the same thing: muted surface, no index numeral, no scale row, no
 * follow-up line — none of the affordances a runnable question carries.
 *
 * THE WORDS ARE NOT OURS. `reason` is the seam's, composed from structural
 * facts and never from the question's content. Nothing here is ever a rewrite
 * of what the writer asked: a repair keeps its second attempt whole, so there
 * is no repaired question this card could attribute to the one it replaced,
 * and it does not pretend otherwise (F8).
 *
 * THE QUESTION LIMIT IS OURS TO SAY (live QA f2). A question set aside because
 * a test asks a limited number of questions, or a follow-up set aside with it,
 * carries a reason code the card keys its own localized copy on. Those had a
 * runnable shape, so the card offers no "we can ask" list for them.
 */

/** Seam reason codes for questions set aside by the per-test question limit. */
const SET_ASIDE_OVER_LIMIT = 'too_many_questions'
const SET_ASIDE_WITH_PARENT = 'follows_set_aside_question'

interface RunGateNoMatchListProps {
  entries: Array<GateNoMatch>
  /** The artifact's position in the gate list. Namespaces every `data-testid`. */
  position: number
  /** The run's per-test question limit, for the set-aside copy. */
  maxQuestions?: number
}

export function RunGateNoMatchList(props: RunGateNoMatchListProps) {
  const { entries, position, maxQuestions = TWIN_TEST_MAX_QUESTIONS } = props

  const { t } = useTranslation('app')

  const reasonFor = (entry: GateNoMatch): string | null => {
    if (entry.reasonCode === SET_ASIDE_OVER_LIMIT) {
      return t('threads.detail.gate.setAsideOverLimit', { limit: maxQuestions })
    }
    if (entry.reasonCode === SET_ASIDE_WITH_PARENT) {
      return t('threads.detail.gate.setAsideWithParent')
    }
    return entry.reason
  }

  const isSetAside = (entry: GateNoMatch): boolean =>
    entry.reasonCode === SET_ASIDE_OVER_LIMIT || entry.reasonCode === SET_ASIDE_WITH_PARENT

  return (
    <div className="mt-3" data-testid={`run-gate-nomatch-${position}`}>
      <div className="flex items-baseline justify-between gap-2 pb-1.5">
        <Text size="xs" weight="medium" color="muted" className="leading-5">
          {t('threads.detail.gate.notRun')}
        </Text>
        <Text size="xs" color="muted" className="shrink-0 leading-5">
          {t('threads.detail.gate.couldNotAsk', { count: entries.length })}
        </Text>
      </div>

      <ul className="space-y-2">
        {entries.map((entry, index) => (
          <li
            key={index}
            className="rounded-lg border border-dashed border-border bg-muted/40 p-3"
            data-testid={`run-gate-nomatch-item-${position}-${index}`}
          >
            <div className="flex items-center gap-2">
              {entry.title ? (
                <span
                  className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground"
                  data-testid={`run-gate-nomatch-title-${position}-${index}`}
                >
                  {entry.title}
                </span>
              ) : (
                <span className="min-w-0 flex-1" />
              )}
              {entry.askedType ? (
                <Badge
                  variant="tag"
                  className="shrink-0 opacity-70"
                  data-testid={`run-gate-nomatch-type-${position}-${index}`}
                >
                  {gateQuestionTypeLabel(entry.askedType)}
                </Badge>
              ) : null}
            </div>

            {/* In full, like a runnable prompt: the reviewer is being told this
                exact question is missing from what they are approving. */}
            <Text
              size="sm"
              color="muted"
              className="mt-2 leading-6"
              data-testid={`run-gate-nomatch-prompt-${position}-${index}`}
            >
              {entry.prompt}
            </Text>

            {reasonFor(entry) ? (
              <Text
                size="xs"
                color="muted"
                className="mt-2 leading-5"
                data-testid={`run-gate-nomatch-reason-${position}-${index}`}
              >
                {reasonFor(entry)}
              </Text>
            ) : null}

            {entry.supportedTypes.length > 0 && !isSetAside(entry) ? (
              <Text
                size="xs"
                color="muted"
                className="mt-1.5 leading-5"
                data-testid={`run-gate-nomatch-supported-${position}-${index}`}
              >
                {t('threads.detail.gate.weCanAsk', {
                  types: entry.supportedTypes.map(gateQuestionTypeLabel).join(', '),
                })}
              </Text>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  )
}
